import { useState } from 'react';
import { useCountdown } from 'src/common/useCountdown';
import {
  Box,
  Button,
  CloseButton,
  Flex,
  Modal,
  Paper,
  ScrollArea,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { useClipboard } from '@mantine/hooks';
import { IconMail, IconCheck, IconCopy } from '@tabler/icons-react';
import type { EmailContentResponse } from 'src/meeting-note/hooks/useGenerateEmailContent';
import ConfirmDeleteModal from 'src/common/components/ConfirmDeleteModal';
import { buildMailtoUrl, truncateForMailto } from 'src/common/utils/email/mailto';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';

const MAILTO_MAX_BODY_LENGTH = 1800;
const MAILTO_HINT_DURATION_SECONDS = 10;

type EmailContentModalProps = {
  opened: boolean;
  onClose: () => void;
  content: EmailContentResponse | null;
  onDelete: () => void;
  isDeleting: boolean;
  clientName?: string;
  documentLabel?: string;
};

type CopyableFieldProps = {
  label: string;
  value: string;
  copied: boolean;
  onCopy: () => void;
};

function CopyableField({ label, value, copied, onCopy }: CopyableFieldProps) {
  return (
    <Paper
      bg="white"
      p="md"
      radius="md"
      pos="relative"
      onClick={onCopy}
      style={{ cursor: 'pointer' }}
    >
      <Button
        variant="subtle"
        color={copied ? 'teal' : 'gray'}
        size="compact-sm"
        pos="absolute"
        top={12}
        right={12}
        leftSection={copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
        onClick={e => {
          e.stopPropagation();
          onCopy();
        }}
      >
        {copied ? 'Copied' : 'Copy'}
      </Button>
      <Text size="sm" fw={FONT_WEIGHTS.semibold} mb="xxxs">
        {label}
      </Text>
      <Text size="sm" pr="xl" style={{ whiteSpace: 'pre-wrap' }}>
        {value}
      </Text>
    </Paper>
  );
}

type CopyableBodyFieldProps = {
  value: string;
  copied: boolean;
  onCopy: () => void;
};

function CopyableBodyField({ value, copied, onCopy }: CopyableBodyFieldProps) {
  return (
    <Paper
      bg="white"
      p="md"
      radius="md"
      pos="relative"
      flex={1}
      mih={0}
      onClick={onCopy}
      style={{ cursor: 'pointer', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
    >
      <Button
        variant="subtle"
        color={copied ? 'teal' : 'gray'}
        size="compact-sm"
        pos="absolute"
        top={12}
        right={12}
        leftSection={copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
        onClick={e => {
          e.stopPropagation();
          onCopy();
        }}
      >
        {copied ? 'Copied' : 'Copy'}
      </Button>
      <Text size="sm" fw={FONT_WEIGHTS.semibold} mb="xxxs">
        Body
      </Text>
      <Box flex={1} mih={0}>
        <ScrollArea h="100%">
          <Text size="sm" pr="md" style={{ whiteSpace: 'pre-wrap' }}>
            {value}
          </Text>
        </ScrollArea>
      </Box>
    </Paper>
  );
}

type InfoPanelProps = {
  clientName?: string;
  documentLabel?: string;
  bodyTooLong: boolean;
  onOpenInClient: () => void;
  onDeleteClick: () => void;
};

function InfoPanel({
  clientName,
  documentLabel,
  bodyTooLong,
  onOpenInClient,
  onDeleteClick,
}: InfoPanelProps) {
  const [showHint, setShowHint] = useState(false);
  const hintCountdown = useCountdown(MAILTO_HINT_DURATION_SECONDS, showHint, () =>
    setShowHint(false),
  );

  const handleOpenInClient = () => {
    onOpenInClient();
    setShowHint(true);
  };

  return (
    <Flex
      direction="column"
      flex={2}
      p="xl"
      bg="white"
      style={{
        borderTopLeftRadius: 'var(--mantine-radius-md)',
        borderBottomLeftRadius: 'var(--mantine-radius-md)',
      }}
    >
      <Box>
        <Title order={4} fw={FONT_WEIGHTS.semibold}>
          Email draft{clientName ? ` for ${clientName}` : ''}
        </Title>
        {documentLabel && (
          <>
            <Text size="sm" c="dimmed" mt="xs">
              Prepared from:
            </Text>
            <Text size="sm" mt="xxxs">
              {documentLabel}
            </Text>
          </>
        )}
      </Box>

      <Box flex={1} />

      <Stack gap="xs">
        {showHint && hintCountdown > 0 && (
          <Text size="sm" c="dimmed">
            Your email app should now be opening with a draft message. If nothing appears, your
            computer may not have a default email application set, or you may be using webmail in
            your browser. You can use the{' '}
            <Text fw={FONT_WEIGHTS.bold} span>
              Copy
            </Text>{' '}
            option below to paste the subject and message into your email instead.
          </Text>
        )}
        <Button
          variant="filled-pill"
          fullWidth
          size="lg"
          leftSection={<IconMail size={18} />}
          onClick={handleOpenInClient}
          disabled={bodyTooLong}
        >
          Open in email client
        </Button>
        <Button variant="subtle" color="red" fullWidth onClick={onDeleteClick}>
          Delete draft
        </Button>
      </Stack>
    </Flex>
  );
}

type ContentPanelProps = {
  recipientEmail: string;
  subject: string;
  body: string;
  onClose: () => void;
};

function ContentPanel({ recipientEmail, subject, body, onClose }: ContentPanelProps) {
  const clipboard = useClipboard();
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const handleCopy = (field: string, value: string) => {
    clipboard.copy(value);
    setCopiedField(field);
  };

  return (
    <Flex
      direction="column"
      flex={3}
      bg="gray.0"
      p="xl"
      pos="relative"
      mih={0}
      style={{
        borderTopRightRadius: 'var(--mantine-radius-md)',
        borderBottomRightRadius: 'var(--mantine-radius-md)',
        overflow: 'hidden',
      }}
    >
      <CloseButton
        c="var(--mantine-color-gray-5)"
        onClick={onClose}
        pos="absolute"
        top={16}
        right={16}
      />

      <Text size="sm" c="dimmed" mb="md">
        Tap any field to copy the text
      </Text>

      <Stack gap="sm" flex={1} mih={0} style={{ overflow: 'hidden' }}>
        <CopyableField
          label="To"
          value={recipientEmail}
          copied={copiedField === 'to'}
          onCopy={() => handleCopy('to', recipientEmail)}
        />
        <CopyableField
          label="Subject"
          value={subject}
          copied={copiedField === 'subject'}
          onCopy={() => handleCopy('subject', subject)}
        />
        <CopyableBodyField
          value={body}
          copied={copiedField === 'body'}
          onCopy={() => handleCopy('body', body)}
        />
      </Stack>
    </Flex>
  );
}

export default function EmailContentModal({
  opened,
  onClose,
  content,
  onDelete,
  isDeleting,
  clientName,
  documentLabel,
}: EmailContentModalProps) {
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  if (!content) return null;

  const { recipientEmail, subject, body } = content;
  const bodyTooLong = body.length > MAILTO_MAX_BODY_LENGTH;

  const handleOpenInClient = () => {
    const truncatedBody = truncateForMailto(body);
    const url = buildMailtoUrl(recipientEmail, subject, truncatedBody);
    window.open(url, '_self');
  };

  return (
    <>
      <Modal
        opened={opened}
        onClose={onClose}
        withCloseButton={false}
        centered
        size="xxl"
        variant="multi"
      >
        <Flex direction="row" mih={400} mah="80vh">
          <InfoPanel
            clientName={clientName}
            documentLabel={documentLabel}
            bodyTooLong={bodyTooLong}
            onOpenInClient={handleOpenInClient}
            onDeleteClick={() => setDeleteModalOpen(true)}
          />
          <ContentPanel
            recipientEmail={recipientEmail}
            subject={subject}
            body={body}
            onClose={onClose}
          />
        </Flex>
      </Modal>

      <ConfirmDeleteModal
        opened={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={() => {
          setDeleteModalOpen(false);
          onDelete();
        }}
        title="Delete email"
        message="Are you sure you want to delete this email? You will need to regenerate it if you want to send it later."
        isLoading={isDeleting}
      />
    </>
  );
}
